/**
 * DataTableContent Component
 * 
 * Renders table with headers, body, skeleton loading, and empty state.
 * Supports virtualization for large datasets (auto-enabled above threshold).
 * Keyboard: ↑↓ rows, 1-9+↑↓ sort, Space actions, Enter select, Esc close
 * 
 * Pagination is handled by DataTableFooter (composed alongside this component).
 * Pagination keyboard shortcuts (←→ pages, Option+[/] per-page) are handled by DataTableShortcuts.
 */

import { type ReactNode, useCallback, useMemo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useThrottleCheck } from '@hooks/useThrottle';
import { flexRender, type Header } from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DesignedScrollArea } from '@/react/components/ui/designed-scroll-area';
import { cn } from '@/lib/utils';
import { useDataTable } from './DataTableProvider';
import { EmptySkuIcon } from '../icons/EmptySkuIcon';
import { 
  DEFAULT_SKELETON_ROWS,
  DEFAULT_COLUMN_SIZE,
  ACTIONS_COLUMN_WIDTH,
  TABLE_HEADER_HEIGHT,
  TABLE_CELL_CLASSES,
  TABLE_BORDER_CLASSES,
  LAST_CELL_GUTTER_CLASSES,
  SHORTCUT_THROTTLE_MS,
  PANEL_THROTTLE_MS,
  VIRTUALIZATION_THRESHOLD,
  ESTIMATED_ROW_HEIGHT,
} from './constants';
import type { DataTableColumnMeta } from './types';
import { Bar } from '../Skeleton/primitives';


export interface DataTableContentProps<TData = unknown> {
  readonly className?: string;
  /** Number of skeleton rows to show when loading */
  readonly skeletonRows?: number;
  /** Custom empty state component */
  readonly emptyState?: ReactNode;
  /** Empty state title */
  readonly emptyTitle?: string;
  /** Row click handler (for navigation) */
  readonly onRowClick?: (row: TData) => void;
  /** Row select handler (Enter key - opens detail panel) */
  readonly onRowSelect?: (row: TData, rowIndex: number) => void;
  /** Ref to the table container (for external focus management) */
  readonly tableRef?: React.RefObject<HTMLDivElement | null>;
  /** Custom row className based on row data */
  readonly getRowClassName?: (row: TData) => string;
  /** Custom skeleton cell renderer. Cells render inside fixed-width columns; keep content within the column width (e.g. max-w-full). */
  readonly renderSkeletonCell?: (column: Header<TData, unknown>['column'], colIndex: number) => ReactNode;
  readonly skeletonRowClassName?: string;
  /** Maximum height before scrolling (enables sticky footer) */
  readonly maxHeight?: string;
  /** Minimum table width in px; lets an unsized flex column keep its room once the sized columns overflow */
  readonly minTableWidth?: number;
  /** Enable row virtualization (auto-enabled when rows > VIRTUALIZATION_THRESHOLD) */
  readonly virtualize?: boolean;
  /**
   * Height in pixels the virtualizer assumes for rows it hasn't rendered yet.
   * Too low and the scrollbar jumps as rows measure themselves on the way in.
   * Defaults to ESTIMATED_ROW_HEIGHT; pass this table's real row height when
   * it differs from that default.
   */
  readonly estimatedRowHeight?: number;
}

function getColumnWidth<TData>(header: Header<TData, unknown>, isLast: boolean): number | undefined {
  if (header.getSize() !== DEFAULT_COLUMN_SIZE) return header.getSize();
  if (isLast && header.column.id === 'actions') return ACTIONS_COLUMN_WIDTH;
  return undefined;
}

export function renderImageNameSkeletonCell(_column: unknown, colIndex: number) {
  if (colIndex === 0) {
    return (
      <div className="flex items-center gap-3 min-w-0 overflow-hidden">
        <div className="w-12 h-12 bg-dinnerplate-white rounded-full" />
        <div className="h-4 bg-dinnerplate-white rounded w-32 max-w-full" />
      </div>
    );
  }
  return <div className="h-4 bg-dinnerplate-white rounded w-3/4" />;
}

function DefaultEmptyState({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full py-16">
      <EmptySkuIcon className="mb-4 w-16 h-16 text-grey" />
      <p className="font-inter text-sm text-grey">{title}</p>
    </div>
  );
}

function EmptyOverlay({ scrollRef, children }: { scrollRef: React.RefObject<HTMLElement | null>; children: ReactNode }) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl || e.deltaX === 0) return;
      e.preventDefault();
      scrollEl.scrollLeft += e.deltaX;
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [scrollRef]);

  return (
    <div ref={overlayRef} style={{ top: TABLE_HEADER_HEIGHT }} className="absolute inset-0 z-10 flex items-center justify-center bg-off-white">
      {children}
    </div>
  );
}

export function DataTableContent<TData = unknown>({
  className,
  skeletonRows = DEFAULT_SKELETON_ROWS,
  emptyState,
  emptyTitle = 'No results found',
  onRowClick,
  onRowSelect,
  tableRef,
  getRowClassName,
  renderSkeletonCell,
  skeletonRowClassName,
  maxHeight,
  minTableWidth,
  virtualize,
  estimatedRowHeight = ESTIMATED_ROW_HEIGHT,
}: DataTableContentProps<TData>) {
  const { table, isLoading, isFetching } = useDataTable<TData>();

  const rows = table.getRowModel().rows;
  const showSkeleton = isLoading && rows.length === 0;

  const isEmpty = rows.length === 0 && !showSkeleton;

  // Check if any columns have frozen meta (used for minWidth on headers)
  const hasFrozenColumns = useMemo(() => {
    const headers = table.getHeaderGroups()[0]?.headers ?? [];
    return headers.some((h) => (h.column.columnDef.meta as DataTableColumnMeta | undefined)?.frozen);
  }, [table]);

  // Compute frozen column positions from column meta.frozen (skip when empty)
  const frozenInfo = useMemo(() => {
    const map = new Map<string, { leftOffset: number; isLastFrozen: boolean }>();
    if (isEmpty) return map;

    const headers = table.getHeaderGroups()[0]?.headers ?? [];
    let leftOffset = 0;
    const frozenEntries: Array<{ id: string; offset: number }> = [];

    for (const header of headers) {
      const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined;
      if (meta?.frozen) {
        frozenEntries.push({ id: header.column.id, offset: leftOffset });
      }
      leftOffset += header.getSize();
    }

    frozenEntries.forEach(({ id, offset }, idx) => {
      map.set(id, { leftOffset: offset, isLastFrozen: idx === frozenEntries.length - 1 });
    });

    return map;
  }, [table, isEmpty]);
  
  // Auto-enable virtualization when row count exceeds threshold
  const shouldVirtualize = virtualize ?? rows.length > VIRTUALIZATION_THRESHOLD;

  const handleRowClick = useCallback(
    (original: TData) => {
      onRowClick?.(original);
    },
    [onRowClick]
  );

  const heldNumberRef = useRef<number | null>(null);
  const canTriggerShortcut = useThrottleCheck(SHORTCUT_THROTTLE_MS);
  const canTriggerPanel = useThrottleCheck(PANEL_THROTTLE_MS);
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
  const isTableHoveredRef = useRef(false);
  const scrollContainerRef = useRef<HTMLElement>(null);
  const sortableColumns = useMemo(
    () => table.getAllColumns().filter((col) => col.getCanSort()),
    [table]
  );

  // Refs for keyboard effect to avoid re-registering listeners on every render
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const focusedRowIndexRef = useRef(focusedRowIndex);
  focusedRowIndexRef.current = focusedRowIndex;
  const sortableColumnsRef = useRef(sortableColumns);
  sortableColumnsRef.current = sortableColumns;
  
  const setScrollContainerRef = useCallback((node: HTMLElement | null) => {
    (scrollContainerRef as React.MutableRefObject<HTMLElement | null>).current = node;
    if (tableRef) {
      (tableRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  }, [tableRef]);

  // Row virtualizer for large datasets
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: 5,
    enabled: shouldVirtualize && !showSkeleton && rows.length > 0,
  });

  // Keyboard navigation — row focus, sorting, row actions (not pagination)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const currentRows = rowsRef.current;
      const currentFocused = focusedRowIndexRef.current;

      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      const isInDropdown = target.closest('[role="menu"]') !== null;
      const isInPopover = target.closest('[data-radix-popper-content-wrapper]') !== null;
      const hasOpenDropdown = document.querySelector('[data-state="open"][role="menu"]') !== null;

      if (e.key === 'Escape') {
        e.preventDefault();
        const actionsButton = target.closest('[data-row-actions-trigger]') as HTMLElement;
        const currentRow = target.closest('tr[data-row-index]') as HTMLElement;

        if (hasOpenDropdown || isInDropdown || isInPopover || actionsButton) {
          (document.activeElement as HTMLElement)?.blur();
          if (currentRow) {
            requestAnimationFrame(() => currentRow.focus());
          } else if (currentFocused !== null) {
            requestAnimationFrame(() => {
              const rowEl = scrollContainerRef.current?.querySelector(`[data-row-index="${currentFocused}"]`) as HTMLElement;
              rowEl?.focus();
            });
          }
          return;
        }
        (document.activeElement as HTMLElement)?.blur();
        setFocusedRowIndex(null);
        return;
      }

      if (isInDropdown || isInPopover) return;

      const num = parseInt(e.key);
      if (num >= 1 && num <= 9) {
        heldNumberRef.current = num;
        return;
      }

      if (heldNumberRef.current !== null && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        if (hasOpenDropdown) return;
        const colIndex = heldNumberRef.current - 1;
        const column = sortableColumnsRef.current[colIndex];
        if (column) {
          e.preventDefault();
          const desc = e.key === 'ArrowDown';
          column.toggleSorting(desc);
        }
        return;
      }

      const isRowFocused = target.closest('tr[data-row-index]') !== null;
      if ((isTableHoveredRef.current || isRowFocused) && currentRows.length > 0 && !hasOpenDropdown) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setFocusedRowIndex(prev => {
            const next = prev === null ? 0 : Math.min(prev + 1, currentRows.length - 1);
            requestAnimationFrame(() => {
              const rowEl = scrollContainerRef.current?.querySelector(`[data-row-index="${next}"]`) as HTMLElement;
              if (rowEl) { rowEl.focus(); rowEl.scrollIntoView({ block: 'nearest', behavior: 'auto' }); }
            });
            return next;
          });
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setFocusedRowIndex(prev => {
            const next = prev === null ? currentRows.length - 1 : Math.max(prev - 1, 0);
            requestAnimationFrame(() => {
              const rowEl = scrollContainerRef.current?.querySelector(`[data-row-index="${next}"]`) as HTMLElement;
              if (rowEl) { rowEl.focus(); rowEl.scrollIntoView({ block: 'nearest', behavior: 'auto' }); }
            });
            return next;
          });
          return;
        }

        if (e.key === 'Enter' && currentFocused !== null && onRowSelect) {
          if (!canTriggerPanel()) return;
          e.preventDefault();
          const row = currentRows[currentFocused];
          if (row) {
            onRowSelect(row.original, currentFocused);
          }
          return;
        }

        if (e.key === ' ' && currentFocused !== null) {
          if (!canTriggerPanel()) return;
          const rowEl = scrollContainerRef.current?.querySelector(`[data-row-index="${currentFocused}"]`);
          const actionsButton = rowEl?.querySelector('[data-row-actions-trigger]') as HTMLButtonElement;
          if (actionsButton) {
            e.preventDefault();
            actionsButton.focus();
            const spaceEvent = new KeyboardEvent('keydown', {
              key: ' ',
              code: 'Space',
              bubbles: true,
              cancelable: true,
            });
            actionsButton.dispatchEvent(spaceEvent);
          }
          return;
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9 && heldNumberRef.current === num) {
        heldNumberRef.current = null;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [onRowSelect, canTriggerShortcut, canTriggerPanel]);

  useEffect(() => {
    setFocusedRowIndex(null);
  }, [rows.length]);

  const leafHeaders = table.getLeafHeaders();

  const [isScrollable, setIsScrollable] = useState(true);
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const check = () => setIsScrollable(el.scrollHeight > el.clientHeight);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [rows.length]);

  // Measure the sticky header so the scrollbar lane starts exactly at its
  // bottom border — the rendered height varies with fonts/padding, so the
  // approximate TABLE_HEADER_HEIGHT constant would leave a visible gap.
  // (The header's bottom line is an inset shadow on the cells, painted
  // inside the measured height, so no border offset is needed.)
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(TABLE_HEADER_HEIGHT);
  useLayoutEffect(() => {
    const el = theadRef.current;
    if (!el) return;
    const measure = () => setHeaderHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={cn('relative flex flex-col flex-1 min-h-0 gap-6', className)}
      onMouseEnter={() => { isTableHoveredRef.current = true; }}
      onMouseLeave={() => {
        isTableHoveredRef.current = false;
        setFocusedRowIndex(null);
      }}
    >
      {/* Table container with border and rounded corners */}
      <div
        className={cn(
          `relative border ${TABLE_BORDER_CLASSES} rounded-input bg-off-white flex flex-col flex-1 min-h-0`,
          'overflow-hidden',
          isFetching && !showSkeleton && 'opacity-70 transition-opacity',
        )}
        style={maxHeight ? { maxHeight } : undefined}
      >
      {isEmpty && (
        <EmptyOverlay scrollRef={scrollContainerRef}>
          {emptyState ?? <DefaultEmptyState title={emptyTitle} />}
        </EmptyOverlay>
      )}
      {/* Custom overlay scrollbar: the vertical lane starts below the sticky
          header and carries the left-divider hairline, which a native
          scrollbar (always spanning the full scroll container) cannot do. */}
      <DesignedScrollArea
        axis="both"
        className="flex-1 min-h-0"
        vScrollbarTop={headerHeight}
        vLaneBorder
        scrollbarLabel="Data table"
        scrollRef={setScrollContainerRef}
      >
        <Table
          className={cn('table-fixed', LAST_CELL_GUTTER_CLASSES)}
          style={minTableWidth ? { minWidth: minTableWidth } : undefined}
        >
          <colgroup>
            {leafHeaders.map((header, index) => (
              <col key={header.id} style={{ width: getColumnWidth(header, index === leafHeaders.length - 1) }} />
            ))}
          </colgroup>
          <TableHeader ref={theadRef} className="bg-dinnerplate-white-light sticky top-0 z-20 [&_tr]:border-b-0">
            {table.getHeaderGroups().map((headerGroup) => (
              // border-b-0 + inset shadow on the cells: with border-collapse,
              // a row border is painted at the row boundary in the table's
              // border layer and scrolls away while the sticky header stays —
              // the shadow is painted inside the cells, so it travels with
              // them and the header keeps its bottom line while scrolling.
              <TableRow
                key={headerGroup.id}
                className={`${TABLE_BORDER_CLASSES} border-b-0 hover:bg-transparent`}
              >
                {headerGroup.headers.map((header, index) => {
                  const isLast = index === headerGroup.headers.length - 1;
                  const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined;
                  const frozen = frozenInfo.get(header.column.id);
                  const colWidth = getColumnWidth(header, isLast);
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        width: colWidth,
                        minWidth: hasFrozenColumns ? colWidth : undefined,
                        left: frozen ? frozen.leftOffset : undefined,
                      }}
                      className={cn(
                        TABLE_CELL_CLASSES,
                        'font-inter text-sm font-semibold text-grey bg-dinnerplate-white-light whitespace-nowrap',
                        'shadow-[inset_0_-1px_0_0_theme(colors.dinnerplate-white)]',
                        meta?.align === 'right' ? 'text-right' : 'text-left',
                        frozen && 'sticky z-30 max-md:static',
                        meta?.headerClassName,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {showSkeleton ? (
              Array.from({ length: skeletonRows }, (_, rowIndex) => (
                <TableRow
                  key={`skeleton-${rowIndex}`}
                  className={cn(TABLE_BORDER_CLASSES, skeletonRowClassName)}
                >
                  {leafHeaders.map((header, colIndex) => {
                    const column = header.column;
                    const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
                    return (
                      <TableCell key={column.id} className={cn(TABLE_CELL_CLASSES, meta?.align === 'right' && 'text-right', meta?.className)}>
                        {renderSkeletonCell
                          ? renderSkeletonCell(column, colIndex)
                          : column.id === 'actions' || column.id === 'select'
                            ? null
                            : <Bar className={cn('h-4 w-3/4 max-w-full', meta?.align === 'right' && 'ml-auto')} />}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : rows.length === 0 ? null
            : shouldVirtualize ? (
              /* Virtualized rows for large datasets */
              (() => {
                const virtualItems = rowVirtualizer.getVirtualItems();
                return (
              <>
                {virtualItems.length > 0 && (
                  <tr style={{ height: `${virtualItems[0]?.start ?? 0}px` }} />
                )}
                {virtualItems.map((virtualRow) => {
                  const row = rows[virtualRow.index];
                  const rowIndex = virtualRow.index;
                  return (
                    <TableRow
                      key={row.id}
                      data-row-index={rowIndex}
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                      tabIndex={0}
                      onClick={onRowClick ? () => handleRowClick(row.original) : undefined}
                      onFocus={() => setFocusedRowIndex(rowIndex)}
                      onBlur={(e) => {
                        if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) {
                          setFocusedRowIndex(null);
                        }
                      }}
                      className={cn(
                        TABLE_BORDER_CLASSES,
                        'group',
                        'has-[[data-state="open"]]:bg-dinnerplate-white-lighter has-[[data-state="open"]]:border-dinnerplate-white',
                        onRowClick && 'cursor-pointer',
                        getRowClassName?.(row.original),
                        focusedRowIndex === rowIndex && 'bg-dinnerplate-white-lighter border-dinnerplate-white',
                        'outline-none',
                        virtualRow.index === rows.length - 1 && 'border-b-0',
                      )}
                    >
                      {row.getVisibleCells().map((cell, cellIdx) => {
                        const isLast = cellIdx === row.getVisibleCells().length - 1;
                        const meta = cell.column.columnDef.meta as DataTableColumnMeta | undefined;
                        const frozen = frozenInfo.get(cell.column.id);
                        const stickyBg = focusedRowIndex === rowIndex
                          ? 'bg-dinnerplate-white-lighter'
                          : 'bg-off-white group-hover:bg-dinnerplate-white-lighter group-has-[[data-state="open"]]:bg-dinnerplate-white-lighter group-data-[state=selected]:bg-dinnerplate-white-lighter';
                        return (
                          <TableCell
                            key={cell.id}
                            style={frozen ? { left: frozen.leftOffset } : undefined}
                            className={cn(
                              TABLE_CELL_CLASSES,
                              meta?.align === 'right' && 'text-right',
                              isLast && !frozen && cell.column.id === 'actions' && cn('sticky right-0', stickyBg),
                              frozen && cn('sticky z-10 max-md:static', stickyBg),
                              meta?.className,
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {virtualItems.length > 0 && (
                  <tr style={{ height: `${rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0)}px` }} />
                )}
              </>
                );
              })()
            ) : (
              /* Standard rows for small datasets */
              rows.map((row, rowIndex) => (
                <TableRow
                  key={row.id}
                  data-row-index={rowIndex}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  tabIndex={0}
                  onClick={onRowClick ? () => handleRowClick(row.original) : undefined}
                  onFocus={() => setFocusedRowIndex(rowIndex)}
                  onBlur={(e) => {
                    if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) {
                      setFocusedRowIndex(null);
                    }
                  }}
                  className={cn(
                    TABLE_BORDER_CLASSES,
                    'group',
                    'has-[[data-state="open"]]:bg-dinnerplate-white-lighter has-[[data-state="open"]]:border-dinnerplate-white',
                    onRowClick && 'cursor-pointer',
                    getRowClassName?.(row.original),
                    focusedRowIndex === rowIndex && 'bg-dinnerplate-white-lighter border-dinnerplate-white',
                    'outline-none',
                    rowIndex === rows.length - 1 && isScrollable && 'border-b-0',
                  )}
                >
                  {row.getVisibleCells().map((cell, cellIdx) => {
                    const isLast = cellIdx === row.getVisibleCells().length - 1;
                    const meta = cell.column.columnDef.meta as DataTableColumnMeta | undefined;
                    const frozen = frozenInfo.get(cell.column.id);
                    const stickyBg = focusedRowIndex === rowIndex
                      ? 'bg-dinnerplate-white-lighter'
                      : 'bg-off-white group-hover:bg-dinnerplate-white-lighter group-has-[[data-state="open"]]:bg-dinnerplate-white-lighter group-data-[state=selected]:bg-dinnerplate-white-lighter';
                    return (
                      <TableCell
                        key={cell.id}
                        style={frozen ? { left: frozen.leftOffset } : undefined}
                        className={cn(
                          TABLE_CELL_CLASSES,
                          meta?.align === 'right' ? 'text-right' : 'text-left',
                          isLast && !frozen && cell.column.id === 'actions' && cn('sticky right-0', stickyBg),
                          frozen && cn('sticky z-10 max-md:static', stickyBg),
                          meta?.className,
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </DesignedScrollArea>
      </div>
    </div>
  );
}
